import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Skeleton,
  Paper,
} from "@mui/material";
import { OpenInNew, Refresh } from "@mui/icons-material";
import { groupBuilderResponse } from "@/models/equip-api-response-objects/groupBuilderResponse";
import { submissionOverview } from "@/models/equip-api-response-objects/submissionOverview";
import { GetSubmissionOverview } from "@/services/api/submission-endpoints";

type Order = "asc" | "desc";

export default function CoverageSection({
  items,
  title,
  onOpenCompany,
}: {
  items: groupBuilderResponse | undefined;
  title: string;
  hint?: string;
  onOpenCompany?: (companyUUID: string) => void;
}) {
  const constituents = items?.constituents ?? [];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submissionOverviews, setSubmissionOverviews] = useState<
    { overview: submissionOverview; id: string; ric?: string }[]
  >([]);

  const [orderBy, setOrderBy] = useState<
    "Rating" | "PriceTarget" | "SubmissionDate" | "CoverageSpan"
  >("SubmissionDate");
  const [order, setOrder] = useState<Order>("desc");

  const handleSort = (key: typeof orderBy) => {
    setOrderBy(key);
    setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const reload = async () => {
    if (constituents.length === 0) {
      setSubmissionOverviews([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tasks = constituents.map(async (c) => {
        const overview = await GetSubmissionOverview(c.companyUUID);
        return { overview, id: c.companyUUID, ric: c.ric };
      });
      const results = await Promise.all(
        tasks.map((p) =>
          p
            .then((value) => ({ status: "fulfilled" as const, value }))
            .catch((reason) => ({ status: "rejected" as const, reason }))
        )
      );
      const ok = results.filter((r) => r.status === "fulfilled").map((r) => r.value);

      setSubmissionOverviews(ok);
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0 && ok.length === 0) 
        setError("Unable to load submission overviews.");
    
    } catch (e) {
      setError("Unexpected error loading submission overviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [items?.constituents?.map((c) => c.companyUUID).join("|")]);

  const ratingChip = (rating?: string) => {
    if (!rating) return <Chip size="small" label="—" variant="outlined" />;
    const r = rating.toLowerCase();
    const color: "default" | "success" | "error" =
      r.includes("buy") || r.includes("overweight") || r.includes("outperform")
        ? "success"
        : r.includes("sell") || r.includes("underweight") || r.includes("underperform")
          ? "error"
          : "default";
    return (
      <Chip
        size="small"
        label={rating}
        color={color === "default" ? undefined : color}
        variant={color === "default" ? "outlined" : "filled"}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const formatDate = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  };

  const formatPrice = (value?: number) => {
    if (value === undefined || value === null) return "—";
    try {
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } catch {
      return String(value);
    }
  };

  const coverageCell = (minYear?: number, maxYear?: number) => {
    if (!minYear || !maxYear) return "—";
    return (
      <Typography variant="caption" color="text.secondary">
        {minYear}–{maxYear}
      </Typography>
    );
  };

  // Build a joined view so we always render a row per constituent (even if missing submission)
  const rows = useMemo(() => {
    const index = new Map(submissionOverviews.map((x) => [x.id, x]));
    return constituents.map((c) => {
      const found = index.get(c.companyUUID);
      return {
        id: c.companyUUID,
        ric: c.ric?.toUpperCase() ?? "",
        overview: found?.overview,
      };
    });
  }, [constituents, submissionOverviews]);

  // Sorting
  const sorted = useMemo(() => {
    const val = [...rows];
    const dir = order === "asc" ? 1 : -1;

    val.sort((a, b) => {
      const ao = a.overview;
      const bo = b.overview;

      const safeStr = (s?: string) => (s ?? "").toLowerCase();
      const safeNum = (n?: number) => (typeof n === "number" ? n : Number.NEGATIVE_INFINITY);

      switch (orderBy) {
        case "Rating":
          return safeStr(ao?.["Rating"]) > safeStr(bo?.["Rating"]) ? dir : -dir;
        case "PriceTarget":
          return (safeNum(ao?.["Price Target"]) - safeNum(bo?.["Price Target"])) * dir;
        case "SubmissionDate": {
          const ad = ao?.["Submission Date"]
            ? new Date(ao["Submission Date"]).getTime()
            : -Infinity;
          const bd = bo?.["Submission Date"]
            ? new Date(bo["Submission Date"]).getTime()
            : -Infinity;
          return (ad - bd) * dir;
        }
        case "CoverageSpan": {
          const aspan =
            ao?.["Minimum Data Year"] && ao?.["Maximum Data Year"]
              ? ao["Maximum Data Year"] - ao["Minimum Data Year"]
              : -Infinity;
          const bspan =
            bo?.["Minimum Data Year"] && bo?.["Maximum Data Year"]
              ? bo["Maximum Data Year"] - bo["Minimum Data Year"]
              : -Infinity;
          return (aspan - bspan) * dir;
        }
        default:
          return 0;
      }
    });

    return val;
  }, [rows, orderBy, order]);

  const count = constituents.length;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        mb: 2,
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Chip size="small" label={`${count} ${count === 1 ? "company" : "companies"}`} />
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Refresh submission overviews">
                <IconButton size="small" onClick={reload}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider />

          {/* Empty state */}
          {count === 0 && (
            <Typography variant="body2" align="center" sx={{ color: "text.secondary", py: 2 }}>
              No companies in this group.
            </Typography>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}

          {/* Table */}
          {count > 0 && (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 520, borderRadius: 1.5 }}
            >
              {loading && <LinearProgress />}
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "grey.100",
                        fontWeight: 700,
                        fontSize: 13,
                        color: "text.secondary",
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      RIC
                    </TableCell>

                    <TableCell sx={thSx}>
                      <TableSortLabel
                        active={orderBy === "Rating"}
                        direction={orderBy === "Rating" ? order : "asc"}
                        onClick={() => handleSort("Rating")}
                      >
                        Rating
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={thSx} align="right">
                      <TableSortLabel
                        active={orderBy === "PriceTarget"}
                        direction={orderBy === "PriceTarget" ? order : "asc"}
                        onClick={() => handleSort("PriceTarget")}
                      >
                        Price Target
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={thSx}>
                      <TableSortLabel
                        active={orderBy === "SubmissionDate"}
                        direction={orderBy === "SubmissionDate" ? order : "asc"}
                        onClick={() => handleSort("SubmissionDate")}
                      >
                        Submission Date
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={thSx}>Modeled Years</TableCell>

                    <TableCell sx={thSx}>Periods</TableCell>
                    <TableCell sx={thSx}>FYE</TableCell>
                    <TableCell sx={thSx}>Val Crncy</TableCell>
                    <TableCell sx={thSx}>Rep Crncy</TableCell>
                    <TableCell sx={thSx} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading
                    ? Array.from({ length: Math.min(8, Math.max(3, count)) }).map((_, i) => (
                        <TableRow key={`sk-${i}`} hover>
                          <TableCell sx={stickyRicSx}>
                            <Skeleton width={48} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={140} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton width={24} />
                          </TableCell>
                        </TableRow>
                      ))
                    : sorted.map((r) => {
                        const o = r.overview;
                        const has = !!o;
                        return (
                          <TableRow
                            key={r.id}
                            hover
                            sx={{
                              "&:last-child td": { border: 0 },
                              cursor: has ? "pointer" : "default",
                              "&:hover": { backgroundColor: "grey.50" },
                              opacity: has ? 1 : 0.65,
                            }}
                          >
                            {/* Pinned RIC */}
                            <TableCell sx={stickyRicSx}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                  fontWeight: 600,
                                }}
                              >
                                {r.ric || "—"}
                              </Typography>
                            </TableCell>

                            {/* Rating */}
                            <TableCell>{ratingChip(o?.["Rating"])}</TableCell>

                            {/* Price Target */}
                            <TableCell align="right">
                              {has ? (
                                <Tooltip
                                  title={`${o?.["Valuation Currency"] ?? ""} Price Target`}
                                  placement="top"
                                  arrow
                                >
                                  <span>{formatPrice(o?.["Price Target"])}</span>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </TableCell>

                            {/* Submission Date */}
                            <TableCell>{has ? formatDate(o?.["Submission Date"]) : "—"}</TableCell>

                            {/* Coverage */}
                            <TableCell>
                              {has
                                ? coverageCell(o?.["Minimum Data Year"], o?.["Maximum Data Year"])
                                : "—"}
                            </TableCell>

                            {/* Periods */}
                            <TableCell>
                              {has ? (o?.["Periods"].toUpperCase() ?? "—") : "—"}
                            </TableCell>

                            {/* FYE */}
                            <TableCell>{has ? (o?.["Financial Year End"] ?? "—") : "—"}</TableCell>

                            {/* Valuation CCY */}
                            <TableCell>
                              {has ? (o?.["Valuation Currency"].toUpperCase() ?? "—") : "—"}
                            </TableCell>

                            {/* Reporting CCY */}
                            <TableCell>
                              {has ? (o?.["Reporting Currency"].toUpperCase() ?? "—") : "—"}
                            </TableCell>

                            {/* Actions */}
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              <Tooltip title="Open company">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => onOpenCompany?.(r.id)}
                                    disabled={!onOpenCompany}
                                  >
                                    <OpenInNew fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// Styles reused for column headers and sticky RIC
const thSx = {
  backgroundColor: "grey.100",
  fontWeight: 700,
  fontSize: 13,
  color: "text.secondary",
  whiteSpace: "nowrap" as const,
};

const stickyRicSx = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  backgroundColor: "background.paper",
  whiteSpace: "nowrap" as const,
};
